import { useState, useRef, useEffect, useCallback } from 'react'
import { Play, Pause, Volume2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const SPEEDS = [1, 1.5, 2]

// Play recordings through our own origin, never the raw provider URL. Twilio
// recording media is behind HTTP Basic Auth, so a direct <audio src> to
// api.twilio.com triggers a browser sign-in prompt; the backend proxy streams
// it under the session cookie instead (and redirects for already-public URLs).
export function RecordingPlayer({ callSid }: { callSid: string }) {
  const { t } = useTranslation('calls')
  const src = `/telemetry/calls/${encodeURIComponent(callSid)}/recording`
  const audioRef = useRef<HTMLAudioElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)

  const [playing, setPlaying] = useState(false)
  const [failed, setFailed] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [speedIdx, setSpeedIdx] = useState(0)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onTime = () => setCurrentTime(audio.currentTime)
    const onMeta = () => setDuration(audio.duration)
    const onEnded = () => setPlaying(false)

    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onMeta)
    audio.addEventListener('durationchange', onMeta)
    audio.addEventListener('ended', onEnded)
    return () => {
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onMeta)
      audio.removeEventListener('durationchange', onMeta)
      audio.removeEventListener('ended', onEnded)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFailed(false)
  }, [src])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
      return
    }
    audio.play().then(
      () => setPlaying(true),
      () => {
        setFailed(true)
        setPlaying(false)
      },
    )
  }, [playing])

  const seek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    const bar = progressRef.current
    if (!audio || !bar || !duration) return
    const rect = bar.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * duration
  }, [duration])

  const cycleSpeed = useCallback(() => {
    const next = (speedIdx + 1) % SPEEDS.length
    setSpeedIdx(next)
    if (audioRef.current) {
      audioRef.current.playbackRate = SPEEDS[next]
    }
  }, [speedIdx])

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="flex items-center gap-3 rounded-lg bg-surface/60 px-4 py-3">
      <audio ref={audioRef} src={src} preload="metadata" />

      {failed ? (
        <span className="flex h-9 shrink-0 items-center px-2 text-xs text-text-muted">
          {t('detail.recording.unavailable')}
        </span>
      ) : (
        <button
          onClick={togglePlay}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
          aria-label={playing ? t('detail.recording.pause') : t('detail.recording.play')}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>
      )}

      {/* Progress bar */}
      <div
        ref={progressRef}
        onClick={seek}
        className="relative flex-1 h-1.5 cursor-pointer rounded-full bg-border group"
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width] duration-100"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progress}% - 6px)` }}
        />
      </div>

      {/* Time */}
      <span className="shrink-0 font-mono text-xs text-text-muted tabular-nums">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>

      {/* Speed */}
      <button
        onClick={cycleSpeed}
        className="shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-text-secondary bg-surface-elevated hover:text-primary transition-colors"
      >
        {SPEEDS[speedIdx]}×
      </button>

      <Volume2 className="h-4 w-4 shrink-0 text-text-muted" />
    </div>
  )
}
